import React, { useState } from "react";

import { Button, notification } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { useLoginUserMutation } from "store/api";
import { useDispatch } from "react-redux";
import { login } from "store/slices/index";
import InputField from "components/Common/InputField";
import { Formik } from "formik";
import * as Yup from "yup";
import { ErrorAlert, Logo } from "components/Common";

const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [error, setError] = React.useState<string | null>(null);
  const [isLocked, setIsLocked] = useState(false);

  const [loginUser, { isLoading }] = useLoginUserMutation();

  return (
    <div className="w-full h-full flex items-center justify-center">
      <div className="w-full h-full flex flex-col justify-center">
        <div className="text-center">
          <Logo />
          <h1 className="text-2xl font-bold">Welcome Back!</h1>
          <p className="mb-4">Login to your account</p>
        </div>

        <Formik
          validateOnBlur
          initialValues={{
            account: "",
            password: "",
          }}
          validationSchema={Yup.object().shape({
            account: Yup.string().required("Account number is required"),
            password: Yup.string().required("Password is required"),
          })}
          onSubmit={async (values) => {
            try {
              const res = await loginUser({
                account: values.account,
                password: values.password,
              }).unwrap();

              if (res?.user?.isVerified === "true") {
                dispatch(
                  login({
                    accessToken: res.token,
                    user: res.user,
                    isAuthenticated: true,
                  }),
                );

                notification.success({
                  message: "Login Success",
                  description: "You have successfully logged in",
                });

                navigate("/dashboard");
              } else {
                notification.error({
                  message: "Login Failed",
                  description: "Please verify your email address",
                });
              }
            } catch (error: any) {
              setError(error?.data?.error || "Invalid username or password");
              if (error?.data?.lockoutRemaining) setIsLocked(true);
            }
          }}
        >
          {({ handleSubmit, isValid, dirty }) => (
            <form onSubmit={handleSubmit} className="space-y-3">
              <ErrorAlert type="error" message={error} setMessage={setError} />

              <InputField
                label="Enter Account Number"
                name="account"
                type="text"
                placeholder="Enter account number"
              />
              <InputField
                label="Password"
                name="password"
                type="password"
                placeholder="Enter password"
              />

              <div className="flex items-center justify-end p-3 gap-2">
                {isLocked && (
                  <Link to="/recaptcha" className="text-blue-500">
                    Account Locked?
                  </Link>
                )}
                <Link to="/forgot-password" className="text-blue-500">
                  Forgot password?
                </Link>
              </div>

              <Button
                type="primary"
                htmlType="submit"
                className="w-full h-10"
                loading={isLoading}
                disabled={!isValid || !dirty || isLoading}
              >
                Login
              </Button>
            </form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default LoginForm;
